// G02 Growth System Health Score + G01 Growth Stall & Bottleneck Diagnostic.
import { useMemo, useState } from "react";
import { Radar, Note, ToolSection } from "./ui";
import { Link } from "react-router-dom";
import { publishedBySlug } from "@/data/tools/registry";

const G02_DIMS = [
  { id: "insight", label: "Insight", q: "We size opportunities from current evidence, not opinions", w: 0.12 },
  { id: "strategy", label: "Strategy", q: "We have explicit choices on where to play and how to win", w: 0.13 },
  { id: "portfolio", label: "Portfolio", q: "Every product/SKU has a defined role and earns its place", w: 0.10 },
  { id: "pricing", label: "Pricing", q: "We know pocket price realisation and govern discounts by contribution", w: 0.12 },
  { id: "channels", label: "Channels", q: "We know channel-level economics and partner health", w: 0.11 },
  { id: "sales", label: "Sales", q: "Coverage, conversion and productivity are measured per territory", w: 0.13 },
  { id: "brand", label: "Brand", q: "Customers can articulate how we differ from alternatives", w: 0.10 },
  { id: "digital", label: "Digital", q: "We know unit economics per order and per cohort online", w: 0.10 },
  { id: "governance", label: "Governance", q: "A monthly commercial review drives decisions and owners", w: 0.09 },
];

const SCALE = [
  { v: 0, label: "Not in place" },
  { v: 1, label: "Ad hoc" },
  { v: 2, label: "Defined" },
  { v: 3, label: "Measured" },
  { v: 4, label: "Managed" },
];

const NEXT_TOOLS = {
  insight: "market-sizing-calculator", strategy: "growth-bottleneck-diagnostic", portfolio: "product-portfolio-xray",
  pricing: "discount-damage-calculator", channels: "distributor-performance-scorecard", sales: "sales-productivity-calculator",
  brand: "brand-positioning-stress-test", digital: "ecommerce-profitability-calculator", governance: "commercial-review-dashboard",
};

export function ToolG02() {
  const [answers, setAnswers] = useState({});
  const answered = G02_DIMS.filter((d) => answers[d.id] !== undefined);
  const done = answered.length === G02_DIMS.length;
  const result = useMemo(() => {
    if (!done) return null;
    let score = 0, wsum = 0;
    const dims = G02_DIMS.map((d) => {
      const s = answers[d.id];
      score += (s / 4) * 100 * d.w;
      wsum += d.w;
      return { ...d, score: Math.round((s / 4) * 100) };
    });
    const total = Math.round(score / wsum);
    const weakest = [...dims].sort((a, b) => a.score - b.score).slice(0, 3);
    return { total, dims, weakest };
  }, [answers, done]);

  return (
    <div>
      <div className="space-y-4" data-testid="g02-questions">
        {G02_DIMS.map((d, i) => (
          <div key={d.id} className="border border-zinc-200 bg-white p-5">
            <div className="flex items-baseline gap-3">
              <span className="overline">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-[15px] text-zinc-800 font-medium">{d.q}</p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {SCALE.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  data-testid={`g02-${d.id}-${s.v}`}
                  onClick={() => setAnswers((a) => ({ ...a, [d.id]: s.v }))}
                  className={`h-9 px-3.5 text-xs font-medium border transition-colors ${answers[d.id] === s.v ? "bg-black text-white border-black" : "border-zinc-300 text-zinc-600 hover:border-black hover:text-black"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {result && (
        <div className="mt-10 border border-zinc-200 bg-white p-6 md:p-8" data-testid="g02-result">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <Radar dims={result.dims} />
            <div>
              <div className="overline">Your Growth System Health Score</div>
              <div className="font-serif-display text-6xl mt-2" data-testid="g02-score">{result.total}<span className="text-2xl text-zinc-400">/100</span></div>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                Self-reported capability — not an external benchmark. The value is in the dimension gaps, not the absolute number.
              </p>
            </div>
          </div>
          <ToolSection title="Your three priorities" testid="g02-priorities">
            <div className="space-y-3">
              {result.weakest.map((d, i) => {
                const next = publishedBySlug(NEXT_TOOLS[d.id]);
                return (
                  <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 border border-black/10 p-4">
                    <div>
                      <span className="overline mr-2">Priority {i + 1}</span>
                      <span className="text-sm font-medium">{d.label}</span>
                      <span className="text-xs text-zinc-500 ml-2">scored {d.score}/100</span>
                    </div>
                    {next && (
                      <Link to={next.url} className="text-sm font-medium underline underline-offset-4 hover:text-zinc-600" data-testid={`g02-next-${d.id}`}>
                        Next: {next.name}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </ToolSection>
        </div>
      )}
    </div>
  );
}

const G01_QS = [
  { id: "revenue", q: "What best describes revenue over the last 4 quarters?", opts: [
    { v: "flat", label: "Flat or declining", w: {} },
    { v: "slow", label: "Growing slower than the market", w: {} },
    { v: "volatile", label: "Growing but volatile", w: {} },
  ]},
  { id: "demand", q: "Enquiry / lead / walk-in volume is…", opts: [
    { v: "healthy", label: "Healthy — we get enough interest", w: { sales: 2 } },
    { v: "weak", label: "Weak — not enough demand", w: { demand: 2, fit: 1 } },
    { v: "unknown", label: "We don't measure it", w: { governance: 2 } },
  ]},
  { id: "conversion", q: "Of qualified opportunities, you win…", opts: [
    { v: "low", label: "Less than we should", w: { sales: 2, price: 1 } },
    { v: "ok", label: "A healthy share", w: { demand: 1 } },
    { v: "unknown", label: "We don't track stage conversion", w: { governance: 2 } },
  ]},
  { id: "repeat", q: "Repeat purchase / retention is…", opts: [
    { v: "strong", label: "Strong and improving", w: {} },
    { v: "weak", label: "Weak — customers don't return", w: { product: 2, price: 1 } },
    { v: "unknown", label: "Not measured by cohort", w: { governance: 1 } },
  ]},
  { id: "discounting", q: "Discounting over the last year has…", opts: [
    { v: "grown", label: "Grown — deals need bigger discounts", w: { price: 2, product: 1 } },
    { v: "stable", label: "Stable and governed", w: {} },
    { v: "na", label: "We don't discount", w: {} },
  ]},
  { id: "availability", q: "Product availability / coverage…", opts: [
    { v: "gaps", label: "Gaps — stock-outs or thin coverage", w: { channel: 2 } },
    { v: "fine", label: "Fine — buyers can find us", w: {} },
    { v: "unknown", label: "Not measured", w: { channel: 1, governance: 1 } },
  ]},
];

const G01_LABELS = {
  demand: { label: "Demand generation", tool: "marketing-waste-detector", text: "Interest volume is the constraint — the market isn't hearing from you, or the proposition isn't landing." },
  sales: { label: "Sales conversion", tool: "sales-funnel-leakage", text: "Demand exists but isn't converting — look at funnel stages, follow-up discipline and proposal quality." },
  price: { label: "Pricing & discounting", tool: "discount-damage-calculator", text: "Price pressure and rising discounts suggest a value-story or price-realisation problem." },
  channel: { label: "Distribution & availability", tool: "distributor-performance-scorecard", text: "Buyers can't reliably find or get the product — a coverage and partner-execution issue." },
  product: { label: "Offer / retention", tool: "hidden-revenue-finder", text: "Customers try but don't return — the offer, delivery or value equation needs work." },
  fit: { label: "Market fit", tool: "customer-interview-question-bank", text: "Weak demand despite activity suggests the segment or proposition needs re-validation." },
  governance: { label: "Commercial visibility", tool: "commercial-review-dashboard", text: "Key commercial questions are unmeasured — start by making the funnel visible." },
};

export function ToolG01() {
  const [answers, setAnswers] = useState({});
  const done = Object.keys(answers).length === G01_QS.length;
  const result = useMemo(() => {
    if (!done) return null;
    const scores = {};
    for (const q of G01_QS) {
      const opt = q.opts.find((o) => o.v === answers[q.id]);
      for (const [k, w] of Object.entries(opt.w)) scores[k] = (scores[k] || 0) + w;
    }
    return Object.entries(scores).sort((a, b) => b[1] - a[1]).filter(([, s]) => s > 0).slice(0, 3);
  }, [answers, done]);

  return (
    <div>
      <p className="text-sm text-zinc-600 leading-relaxed max-w-2xl">
        Six questions. The output is a ranked set of hypotheses to investigate — not a diagnosis. A short self-assessment narrows the search; evidence confirms it.
      </p>
      <div className="mt-6 space-y-4" data-testid="g01-questions">
        {G01_QS.map((q, i) => (
          <div key={q.id} className="border border-zinc-200 bg-white p-5">
            <div className="flex items-baseline gap-3">
              <span className="overline">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-[15px] text-zinc-800 font-medium">{q.q}</p>
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              {q.opts.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`g01-${q.id}-${o.v}`}
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.v }))}
                  className={`flex-1 text-left h-auto py-2.5 px-3.5 text-sm border transition-colors ${answers[q.id] === o.v ? "bg-black text-white border-black" : "border-zinc-300 text-zinc-600 hover:border-black hover:text-black"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {result && (
        <div className="mt-10" data-testid="g01-result">
          <div className="overline">Most plausible bottlenecks</div>
          <div className="mt-4 space-y-3">
            {result.map(([k, score], i) => {
              const info = G01_LABELS[k];
              const tool = publishedBySlug(info.tool);
              return (
                <div key={k} className="border border-zinc-200 bg-white p-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="font-serif-display text-xl">{i + 1}. {info.label}</div>
                    <span className="text-xs text-zinc-500">signal strength {score}/4</span>
                  </div>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{info.text}</p>
                  {tool && (
                    <Link to={tool.url} className="mt-3 inline-block text-sm font-medium underline underline-offset-4 hover:text-zinc-600" data-testid={`g01-next-${k}`}>
                      Go deeper: {tool.name}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-6"><Note>Next step: pick the top hypothesis and gather the evidence listed — a week of data beats a month of debate.</Note></div>
        </div>
      )}
    </div>
  );
}
